import React, {useRef} from 'react';
import {Link, useSearchParams} from "react-router-dom";
import {Affix, Badge, Switch, Tabs} from "antd";
import {CheckOutlined, CloseOutlined} from "@ant-design/icons";
import InfoTooltip from "../../../components/InfoTooltip";
import {useTalentCandidateScope} from "./TalentCandidateScopeProvider";
import {usePosition} from "../../../providers/ProjectPositionProvider";
import LoadingSpinner from "../../../components/LoadingSpinner";
import CandidatePayoutForm from "./CandidatePayoutForm";
import CandidateProposalEditTab from "./CandidateProposalEditTab";
import CandidateCoverLetterEditTab from "./CandidateCoverLetterEditTab";

const ManageCandidateScopeTable = ({forAgency,...props}) => {

    const tableRef = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        candidate,
        isLoading: isLoadingCandidate,
        isDisabled,
        onUpdateProposal,
        onChange,
        updateCandidateScope,
        confirmCandidateScope,
        resetModel
    } = useTalentCandidateScope();

    const{position} = usePosition()

    if (isLoadingCandidate || !candidate) {
        return (
            <div className="flex flex-col items-center py-5">
                <LoadingSpinner size="lg"/>
            </div>
        )
    }


    return (
        <div className="flex flex-col items-stretch gap-3">
            <Link to="../" replace={true} className="btn btn-link btn-link-primary self-start">
                <i className="fas fa-caret-left mr-2 no-underline"/>
                Back to Role Scope & Candidates
            </Link>
            <div className="flex flex-col gap-6 items-stretch relative manage-team-scope-table" ref={tableRef}>
                <Affix>
                    <div
                        className="flex flex-col bg-white items-stretch px-4 py-6 gap-4 border-b border-gray-100 drop-shadow-sm min-h-[100px]">
                        <p className="text-base">
                            Project Role Scope for Talent Candidate <span className="text-primary"></span>
                        </p>

                        <div className="flex flex-row items-stretch">

                            <div className="flex-grow flex flex-row justify-start gap-14">
                                {!forAgency && (
                                    <React.Fragment>
                                        {position.fixedFeePricingStrategyIsFullBreakdown() && (
                                            <div className="flex flex-col gap-1.5 items-start">
                                                <p className="text-xs font-semibold">
                                                    Original Total Hours
                                                </p>
                                                <p className="text-primary text-lg">
                                                    {TB.sumBy(TB.filter(position.deliverables || [], ['is_active', true]), 'total_hours')} hours
                                                </p>
                                            </div>
                                        )}
                                        <div className="flex flex-col gap-1.5 items-start">
                                            <p className="text-xs font-semibold flex flex-row gap-1.5 items-center">
                                                <span>
                                                    Original Estimated Client Fee
                                                </span>
                                                <InfoTooltip placement="right" type="dark">
                                                    <span className="text-xs px-2 py-3 text-white">
                                                        Estimated Cost is based on the average Standard Platform Market Rate for each
                                                        role. The cost includes the project markup fee of {(position.percent_markup || 0) * 100}%.
                                                    </span>
                                                </InfoTooltip>
                                            </p>
                                            <p className="text-primary text-lg whitespace-nowrap">
                                                {TB.formatCurrency(position.exact_standard_platform_cost)}
                                            </p>
                                        </div>
                                    </React.Fragment>
                                )}
                                {position.fixedFeePricingStrategyIsFullBreakdown() && (
                                    <div className="flex flex-col gap-1.5 items-start">
                                        <p className="text-xs font-semibold">
                                            Talent Total Hours
                                        </p>
                                        <p className="text-primary text-lg">
                                            {candidate.job_candidate?.proposed_total_periods || candidate.job_candidate?.total_hours} hours
                                        </p>
                                    </div>
                                )}
                                <div className="flex flex-col gap-1.5 items-start">
                                    <p className="text-xs font-semibold flex flex-row gap-1.5 items-center">
                                        <span>
                                            Talent Payout{!forAgency && ' / Client Fee'}
                                        </span>
                                        {!forAgency && (
                                            <InfoTooltip placement="right" type="dark">
                                                <span className="text-xs px-2 py-3 text-white">
                                                    Client Fee is based on the Standard Hourly Rates for each individual
                                                    based on their assigned role(s). The cost includes the project markup
                                                    fee of {TB.formatNumber(candidate.job_candidate.percent_markup * 100, 2)}%.
                                                </span>
                                            </InfoTooltip>
                                        )}
                                    </p>
                                    <p className="text-primary text-lg">
                                        {TB.formatCurrency(candidate.job_candidate?.payout_amount)}
                                        {!forAgency && (
                                            <React.Fragment>
                                                &nbsp;/&nbsp;
                                                {TB.formatCurrency(candidate.job_candidate.platform_cost)}
                                            </React.Fragment>
                                        )}
                                    </p>
                                </div>
                                <div className="flex flex-col gap-1.5 items-start">
                                    <p className="text-xs font-semibold flex flex-row gap-1.5 items-center">
                                        <span>
                                            Candidate Payout Confirmed
                                        </span>
                                    </p>
                                    <p className="text-primary text-lg">
                                        <span className={!isDisabled ? '' : 'pointer-events-none'}>
                                            <Switch
                                                size="large"
                                                checkedChildren={<CheckOutlined/>}
                                                unCheckedChildren={<CloseOutlined/>}
                                                checked={candidate.hasConfirmedPayout()}
                                                disabled={!candidate.canAcceptPayout()}
                                                onChange={(checked, e) => {
                                                    if (!candidate.canAcceptPayout()) {
                                                        e.preventDefault();
                                                    }else confirmCandidateScope()
                                                }}
                                            />
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </Affix>

                <div className="bg-white px-4 py-6 manage-scope-table">

                    {candidate?.hasConfirmedScope() ? (
                        <div className="flex flex-col items-stretch gap-10">
                            <p className="text-lg flex flex-row items-center gap-3">
                                <span className="font-semibold underline">
                                    Candidate Scope
                                </span>
                                <Badge
                                    count={`Pricing Strategy: ${position.getFixedFeePricingStrategyName()}`}
                                    color={'#9f9f9f'}/>
                            </p>
                            <Tabs
                                defaultActiveKey="1"
                                type="card"
                                onChange={key => resetModel()}
                                items={[
                                    {
                                        label: "Candidate Payout",
                                        key: 1,
                                        children: (
                                            <div className="flex flex-col gap-10 items-stretch">
                                                <CandidatePayoutForm forAgency={forAgency}/>
                                            </div>
                                        )
                                    },
                                    {
                                        label: "Cover Letter",
                                        key: 2,
                                        children: (
                                            <div className="py-3 px-4">
                                                <CandidateCoverLetterEditTab/>
                                            </div>
                                        )
                                    },
                                    {
                                        label: "Proposal Document",
                                        key: 3,
                                        children: (
                                            <div className="py-3 px-4">
                                                <CandidateProposalEditTab/>
                                            </div>
                                        )
                                    },
                                ]}
                            />
                        </div>
                    ) : (
                        <React.Fragment>
                            <div className="flex flex-row items-center justify-between mb-4">
                                <p className="text-lg font-semibold underline">
                                    Candidate Scope
                                </p>
                            </div>
                            <div className="flex flex-col gap-10 items-stretch">
                                <CandidatePayoutForm forAgency={forAgency}/>
                            </div>

                        </React.Fragment>
                    )}
                </div>
            </div>
        </div>
    );
};
export default ManageCandidateScopeTable;